import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import API_URL from '../config';
import { getAuthToken } from '../utils/authSession';
import './NotificationBell.css';

// Herbruikbaar notificatie-belletje voor Admin/Dashboard/CleanerDashboard.
// Pollt periodiek het aantal ongelezen notificaties en, bij openen van het
// dropdown-paneel, de volledige lijst (meest recente eerst).
// Let op: de notificatie-berichten zelf (n.message) komen als kant-en-klare
// tekst van de backend en zijn nog niet vertaald - enkel de chrome van dit
// belletje zelf (labels, "Alles gelezen", tijdsaanduiding, ...) is vertaald.
//
// "onNotificationClick" (optioneel): callback die de oproepende pagina kan
// meegeven om te bepalen waar een klik op een melding naartoe navigeert (bv.
// naar de "Ingepland"-tab bij een TASK_CLAIMED-melding) - dit belletje zelf
// kent de tab/paginastructuur van elke context niet, dus laat die keuze aan
// de pagina die het rendert.
// Meldingenlijst staat al klaar nog voor je het scherm opent: dit haalt ze op
// bij het openen van de app en daarna elke 30 seconden op de achtergrond.
let notificationsCache = null;
const fetchNotificationsInto = async () => {
  try {
    const res = await axios.get(`${API_URL}/api/notifications`, { headers: { Authorization: `Bearer ${getAuthToken()}` } });
    notificationsCache = res.data;
    return res.data;
  } catch (e) { return null; }
};
export const startNotificationPrefetch = () => {
  fetchNotificationsInto();
  const id = setInterval(fetchNotificationsInto, 30000);
  return () => clearInterval(id);
};

function NotificationBell({ onNotificationClick, screen = false }) {
  const { t } = useTranslation();
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState(notificationsCache || []);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef(null);

  const authHeaders = () => ({
    headers: { Authorization: `Bearer ${getAuthToken()}` }
  });

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/notifications/unread-count`, authHeaders());
      setUnreadCount(res.data.count || 0);
    } catch (error) {
      // Stil falen: het belletje mag geen fouten tonen aan de gebruiker.
    }
  }, []);

  const fetchNotifications = async () => {
    // Is er al een (voorgeladen) lijst, dan meteen tonen en op de achtergrond verversen.
    if (!notificationsCache) setLoading(true);
    else setNotifications(notificationsCache);
    try {
      const res = await axios.get(`${API_URL}/api/notifications`, authHeaders());
      notificationsCache = res.data;
      setNotifications(res.data);
    } catch (error) {
      // Stil falen
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // "screen"-modus (enkel native app): geen belletje/dropdown, maar de lijst
  // zelf als volledig scherm - direct geladen bij het openen.
  // Bij het openen van het scherm worden alle meldingen meteen als gelezen
  // gemarkeerd op de server (teller verdwijnt), maar in de lijst blijven de
  // meldingen die bij het openen nog ongelezen waren gemarkeerd (blauw); de
  // volgende keer zijn ze wit.
  useEffect(() => {
    if (!screen) return;
    (async () => {
      await fetchNotifications();
      try {
        await axios.post(`${API_URL}/api/notifications/read-all`, {}, authHeaders());
        setUnreadCount(0);
        // Cache: volgende keer zijn ze wit (reeds gelezen).
        if (notificationsCache) notificationsCache = notificationsCache.map((n) => ({ ...n, read: true }));
        window.dispatchEvent(new CustomEvent('smartstays:notifications-read'));
      } catch (e) { /* stil */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen]);

  const toggleOpen = () => {
    const next = !open;
    setOpen(next);
    if (next) fetchNotifications();
  };

  const markAsRead = async (id) => {
    try {
      await axios.post(`${API_URL}/api/notifications/${id}/read`, {}, authHeaders());
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
      fetchUnreadCount();
    } catch (error) {
      // Stil falen
    }
  };

  // Klik op een melding: markeer als gelezen (indien nog niet) en laat de
  // oproepende pagina (indien die dat ondersteunt via "onNotificationClick")
  // naar het relevante onderdeel doorklikken - het dropdown-paneel sluit
  // meteen zodat je meteen ziet waar je terechtkomt.
  const handleNotificationClick = (n) => {
    if (!n.read) markAsRead(n.id);
    if (onNotificationClick) {
      onNotificationClick(n);
      setOpen(false);
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.post(`${API_URL}/api/notifications/read-all`, {}, authHeaders());
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      // Stil falen
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const diffMs = Date.now() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return t('notifications.justNow');
    if (diffMin < 60) return t('notifications.minutesAgo', { count: diffMin });
    const diffHour = Math.floor(diffMin / 60);
    if (diffHour < 24) return t('notifications.hoursAgo', { count: diffHour });
    return date.toLocaleDateString('nl-BE');
  };

  const renderPanel = (className) => (
        <div className={className}>
          <div className="notification-dropdown-header">
            <span>{t('notifications.title')}</span>
            {!screen && unreadCount > 0 && (
              <button className="notification-mark-all" onClick={markAllAsRead}>{t('notifications.markAllRead')}</button>
            )}
          </div>
          <div className="notification-list">
            {loading && <div className="notification-empty" aria-busy="true" />}
            {!loading && notifications.length === 0 && (
              <div className="notification-empty">{t('notifications.none')}</div>
            )}
            {!loading && notifications.map((n) => {
              const severityClass = n.type === 'BATTERY_LOW'
                ? 'notification-critical'
                : n.type === 'BATTERY_WARNING'
                ? 'notification-warning'
                : '';
              return (
                <div
                  key={n.id}
                  className={`notification-item ${n.read ? '' : 'unread'} ${severityClass} ${onNotificationClick ? 'clickable' : ''}`}
                  onClick={() => handleNotificationClick(n)}
                >
                  <p className="notification-message">{n.message}</p>
                  <div className="notification-footer">
                    <span className="notification-time">{formatTime(n.createdAt)}</span>
                    {onNotificationClick && (
                      <svg className="notification-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M9 6L15 12L9 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
  );

  if (screen) return renderPanel('notification-screen');

  return (
    <div className="notification-bell-container" ref={containerRef}>
      <button className="notification-bell-btn" onClick={toggleOpen} aria-label={t('notifications.title')}>
        <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {unreadCount > 0 && <span className="notification-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>}
      </button>

      {open && renderPanel('notification-dropdown')}
    </div>
  );
}

export default NotificationBell;
